import { WorkDay } from '@/modules/WorkDay/types/response'
import {
    getStatusLabel,
    getDayOffTypeLabel,
    getBusinessTypeLabel,
} from '@/modules/WorkDay/constants'
import { formatDate, formatFromUTC } from '@/utils/function'
import React from 'react'
import { useTranslation } from 'react-i18next'

const STATUS_COLORS: Record<string, string> = {
    '': 'text-amber-600',
    approved: 'text-green-600',
    pending: 'text-amber-600',
    rejected: 'text-red-600',
}

const STATUS_BG: Record<string, string> = {
    '': 'bg-amber-50',
    approved: 'bg-green-50',
    pending: 'bg-amber-50',
    rejected: 'bg-red-50',
}

interface WorkDayTimeTooltipProps {
    workDay: WorkDay
    isVisible: boolean
}

const WorkDayTime: React.FC<WorkDayTimeTooltipProps> = ({
    workDay,
    isVisible,
}) => {
    const { t } = useTranslation()

    if (!isVisible) return null

    const isLateCheckIn = () => {
        if (!workDay.work_day_in) return false
        if (workDay.work_day_option !== null && workDay.work_day_option !== 0)
            return false

        const [hours, minutes] = workDay.work_day_in.split(':').map(Number)
        return hours > 8 || (hours === 8 && minutes > 30)
    }

    const calculateWorkHours = () => {
        if (!workDay.work_day_in || !workDay.work_day_out) return '0.00'

        const [inHours, inMinutes] = workDay.work_day_in.split(':').map(Number)
        const [outHours, outMinutes] = workDay.work_day_out
            .split(':')
            .map(Number)

        const inTotalMinutes = inHours * 60 + inMinutes
        const outTotalMinutes = outHours * 60 + outMinutes

        const diffMinutes = outTotalMinutes - inTotalMinutes
        const hours = diffMinutes / 60

        return hours.toFixed(2)
    }
    return (
        <div className="absolute z-10 top-full left-1/2 transform -translate-x-1/2 my-2 p-4 bg-white text-gray-400 rounded-lg shadow-lg text-[12px] min-w-[250px]">
            <div className="grid grid-cols-2 gap-2 text-left">
                {workDay.work_day_date && (
                    <>
                        <div className="text-gray-400">
                            {formatDate(workDay.work_day_date)}
                        </div>
                        <div className="font-medium">
                            {workDay.work_day_type}
                        </div>
                        <div className="col-span-2 border-b border-blue-300 my-2"></div>
                    </>
                )}
                {workDay.work_day_in && (
                    <>
                        <div className="text-black flex items-center gap-1">
                            <span
                                className={`w-3 h-3 rounded-full inline-block ${
                                    isLateCheckIn()
                                        ? 'bg-red-500'
                                        : 'bg-green-500'
                                }`}
                            ></span>
                            {t('day-in')}:
                        </div>
                        <div className="font-medium text-black">
                            {workDay.work_day_in}
                        </div>
                    </>
                )}
                {workDay.work_day_out && (
                    <>
                        <div className="text-black flex items-center gap-1">
                            <span className="w-3 h-3 bg-green-500 rounded-full inline-block"></span>
                            {t('day-out')}:
                        </div>
                        <div className="font-medium text-black">
                            {workDay.work_day_out}
                        </div>
                    </>
                )}
                {(workDay.work_day_in && workDay.work_day_out) ||
                (workDay.work_n_day !== null &&
                    workDay.work_n_day !== undefined) ? (
                    <div className="col-span-2 grid grid-cols-2 gap-4 mt-2">
                        {workDay.work_day_in && workDay.work_day_out && (
                            <div className="text-left">
                                <div className="text-gray-400">
                                    {t('work-hours')}
                                </div>
                                <div className="font-medium text-lg">
                                    {calculateWorkHours()}
                                </div>
                            </div>
                        )}
                        {workDay.work_n_day !== null &&
                            workDay.work_n_day !== undefined && (
                                <div className="text-left">
                                    <div className="text-gray-400">
                                        {t('salary-coefficient')}
                                    </div>
                                    <div className="font-medium text-lg">
                                        {workDay.work_n_day}
                                    </div>
                                </div>
                            )}
                    </div>
                ) : null}
                {/* Related Day Offs */}
                {workDay.related_day_offs &&
                    workDay.related_day_offs.length > 0 && (
                        <div className="col-span-2 mt-2">
                            <div className="col-span-2 border-b border-gray-200 my-1"></div>
                            <div className="text-gray-500 font-medium mb-1">
                                Nghỉ phép liên quan
                            </div>
                            {workDay.related_day_offs.map((dayOff) => (
                                <div
                                    key={dayOff.day_off_id}
                                    className={`p-1.5 rounded mb-1 ${STATUS_BG[dayOff.day_off_status] || 'bg-amber-50'}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-black">
                                            {getDayOffTypeLabel(
                                                dayOff.day_off_type
                                            )}
                                        </span>
                                        <span
                                            className={`text-[10px] font-medium ${STATUS_COLORS[dayOff.day_off_status] || 'text-amber-600'}`}
                                        >
                                            {getStatusLabel(
                                                dayOff.day_off_status
                                            )}
                                        </span>
                                    </div>
                                    {dayOff.option && (
                                        <div className="text-gray-400 text-[10px]">
                                            {dayOff.option === 'morning'
                                                ? 'Buổi sáng'
                                                : dayOff.option === 'afternoon'
                                                  ? 'Buổi chiều'
                                                  : dayOff.option}
                                        </div>
                                    )}
                                    {dayOff.day_off_reason && (
                                        <div className="text-gray-400 text-[10px] truncate max-w-[220px]">
                                            {dayOff.day_off_reason}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                {/* Related Businesses */}
                {workDay.related_businesses &&
                    workDay.related_businesses.length > 0 && (
                        <div className="col-span-2 mt-2">
                            <div className="col-span-2 border-b border-gray-200 my-1"></div>
                            <div className="text-gray-500 font-medium mb-1">
                                Công tác liên quan
                            </div>
                            {workDay.related_businesses.map((biz) => (
                                <div
                                    key={biz.business_id}
                                    className={`p-1.5 rounded mb-1 ${STATUS_BG[biz.business_status] || 'bg-amber-50'}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-black">
                                            {getBusinessTypeLabel(
                                                biz.business_type
                                            )}
                                        </span>
                                        <span
                                            className={`text-[10px] font-medium ${STATUS_COLORS[biz.business_status] || 'text-amber-600'}`}
                                        >
                                            {getStatusLabel(
                                                biz.business_status
                                            )}
                                        </span>
                                    </div>
                                    {biz.business_place && (
                                        <div className="text-gray-400 text-[10px]">
                                            {biz.business_place}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                {workDay.work_day_updated_at && (
                    <div className="col-span-2 mt-2 text-left">
                        <div className="text-gray-400">
                            {t('last-sync-time')}
                        </div>
                        <div className="font-medium text-black">
                            {formatFromUTC(workDay.work_day_updated_at)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default WorkDayTime
